import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from "../../lib/api";
import { fireTradeToast } from "../../lib/tradeToast";
import { summarizeCancelResults } from "../../lib/queuedOrderCancellation";
import "./QueuedTrades.css";

const ACTION_LABELS = {
    SELL_PUT:           "Sell Put",
    SELL_CSP:           "Sell Put",
    SELL_CALL:          "Sell Call",
    BUY_TO_CLOSE:       "Close",
    TAKE_PROFIT:        "Close",
    OPEN_DEBIT_SPREAD:  "Debit Spread",
    CLOSE_DEBIT_SPREAD: "Close Spread",
};

const STRATEGY_LABELS = {
    wheel:           "Wheel Strategy",
    earnings:        "Earnings Strategy",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
    1: "Wheel Strategy",
    2: "Tactical Spread",
    3: "Regime Spread",
    4: "Earnings Strategy",
};

const SPREAD_ACTIONS = new Set(["OPEN_DEBIT_SPREAD", "CLOSE_DEBIT_SPREAD"]);
const WHEEL_ACTIONS  = new Set(["SELL_PUT", "SELL_CSP", "SELL_CALL", "BUY_TO_CLOSE", "TAKE_PROFIT", "CLOSE"]);

function inferStrategyFromAction(action) {
    if (!action) return null;
    if (SPREAD_ACTIONS.has(action)) return "Tactical Spread";
    if (WHEEL_ACTIONS.has(action))  return "Wheel Strategy";
    return null;
}

function resolveStrategyLabel(meta, strategyId, action) {
    const metaKey = meta?.strategy || meta?.selected_strategy;
    if (metaKey && STRATEGY_LABELS[metaKey]) return STRATEGY_LABELS[metaKey];
    if (strategyId != null) {
        const fromId = STRATEGY_LABELS[strategyId];
        if (fromId) return fromId;
        if (typeof strategyId === "string" && strategyId.trim()) return strategyId;
    }
    return inferStrategyFromAction(action);
}


function fmtExp(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtTime(isoStr) {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtCurrency(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolveQueueState(statuses = []) {
    const normalized = statuses
        .map((status) => String(status || "").toLowerCase())
        .filter(Boolean);

    if (normalized.includes("submitting")) {
        return {
            status: "submitting",
            label: "Submitting",
            note: "Already being submitted — cancellation is no longer available.",
        };
    }

    return {
        status: "queued",
        label: "Queued",
        note: "Waiting for market open",
    };
}

// Group orders that share the same symbol + action + strike + expiration.
// Returns an array of group objects with aggregated totals.
function groupOrders(orders) {
    const map = {};
    for (const order of orders) {
        const meta   = order.meta || {};
        const key    = [
            order.underlying_symbol ?? "",
            order.action             ?? "",
            meta.strike_price        ?? "",
            meta.expiration_date     ?? "",
        ].join("|");

        if (!map[key]) {
            map[key] = {
                key,
                underlying_symbol: order.underlying_symbol,
                action:            order.action,
                strategy_id:       order.strategy_id ?? null,
                meta,
                auto_manage:       order.auto_manage === true,
                created_at:        order.created_at,
                ids:               [],
                statuses:          [],
                total_contracts:   0,
                total_premium:     0,
                capital_required:  0,
            };
        }

        const g         = map[key];
        const qty       = Number(order.qty) || 1;
        const premium   = Number(meta.premium ?? order.premium ?? 0);
        const strike    = Number(meta.strike_price ?? 0);

        g.ids.push(order.id);
        g.statuses.push(order.status);
        g.total_contracts += qty;
        g.total_premium   += premium * 100 * qty;
        g.capital_required = strike * 100 * g.total_contracts;

        if (g.strategy_id == null && order.strategy_id != null) g.strategy_id = order.strategy_id;
        if (order.created_at < g.created_at) g.created_at = order.created_at;
    }
    return Object.values(map);
}

const cardVariants = {
    hidden:  { opacity: 0, y: 8 },
    visible: (i) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.06, duration: 0.2, ease: "easeOut" },
    }),
    exit: { opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.18, ease: "easeIn" } },
};

// ------------------------------------------------------------------ //
// Component                                                            //
// ------------------------------------------------------------------ //

const QueuedTrades = ({ portfolioValue, onCountChange, onCapitalChange, refreshKey = 0 }) => {
    const [orders,      setOrders]      = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [cancelling,  setCancelling]  = useState(null);  // group key being cancelled

    const applyOrders = (list) => {
        setOrders(list);
        onCountChange?.(list.length);
        const capital = groupOrders(list).reduce((sum, g) => sum + g.capital_required, 0);
        onCapitalChange?.(capital);
    };

    const refreshOrders = async () => {
        const { data } = await axios.get(buildApiUrl("/pending-orders"), {
            withCredentials: true,
        });
        const list = data?.orders || [];
        applyOrders(list);
        return list;
    };

    useEffect(() => {
        setLoading(true);
        refreshOrders()
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [refreshKey]); // eslint-disable-line

    const handleCancelGroup = async (group) => {
        setCancelling(group.key);
        try {
            const results = await Promise.allSettled(
                group.ids.map((id) =>
                    axios.post(
                        buildApiUrl("/cancel-order"),
                        { order_id: id },
                        { withCredentials: true }
                    )
                )
            );
            const summary = summarizeCancelResults(results);
            try {
                await refreshOrders();
                fireTradeToast(summary.message, summary.ok ? "success" : "error");
            } catch {
                fireTradeToast(
                    "Cancellation was attempted, but the queue could not be refreshed. Please reload to confirm the current state.",
                    "error",
                );
            }
        } finally {
            setCancelling(null);
        }
    };

    if (loading || orders.length === 0) return null;

    const groups        = groupOrders(orders);
    const totalCapital  = groups.reduce((sum, g) => sum + g.capital_required, 0);
    const buyingPower   = portfolioValue != null ? portfolioValue - totalCapital : null;

    return (
        <section className="queued-trades">

            {/* ---- Section header ---- */}
            <div className="qt-header">
                <div className="qt-header-left">
                    <span className="qt-title">Queued</span>
                    <span className="qt-count">{orders.length}</span>
                </div>
                <span className="qt-market-note">
                    <span className="qt-clock-dot" />
                    Market closed · Executes at 9:30 AM
                </span>
            </div>

            {/* ---- Capital summary ---- */}
            <div className="qt-capital-summary">
                <div className="qt-capital-item">
                    <span className="qt-capital-label">Reserved capital</span>
                    <span className="qt-capital-value qt-capital-reserved">
                        ${fmtCurrency(totalCapital)}
                    </span>
                </div>
                <div className="qt-capital-divider" />
                <div className="qt-capital-item">
                    <span className="qt-capital-label">Remaining buying power</span>
                    <span className={`qt-capital-value ${buyingPower != null && buyingPower < totalCapital * 0.5 ? "qt-capital-low" : ""}`}>
                        {buyingPower != null ? `$${fmtCurrency(Math.max(0, buyingPower))}` : "—"}
                    </span>
                </div>
            </div>

            {/* ---- Group cards ---- */}
            <AnimatePresence>
                {groups.map((group, i) => {
                    const meta          = group.meta || {};
                    const label         = ACTION_LABELS[group.action] || group.action;
                    const expLabel      = fmtExp(meta.expiration_date);
                    const isCancelling  = cancelling === group.key;
                    const strategyLabel = resolveStrategyLabel(meta, group.strategy_id, group.action);
                    const modeLabel     = group.auto_manage ? "auto" : "manual";
                    const queueState    = resolveQueueState(group.statuses);

                    const contractLine = [
                        meta.strike_price  != null && `$${meta.strike_price}`,
                        expLabel,
                        meta.dte           != null && `${meta.dte}d`,
                        meta.delta         != null && `Δ ${Number(meta.delta).toFixed(2)}`,
                    ].filter(Boolean).join(" · ");

                    // Reason: preset label + selection logic
                    const preset          = meta.preset
                        ? meta.preset.charAt(0).toUpperCase() + meta.preset.slice(1)
                        : null;
                    const selectionReason = meta.selection_reason || null;
                    const reasonText      = [preset && `${preset} preset`, selectionReason]
                        .filter(Boolean).join(" · ");

                    return (
                        <motion.div
                            key={group.key}
                            className="qt-card"
                            custom={i}
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            layout
                        >
                            {/* Top row */}
                            <div className="qt-card-top">
                                <div className="qt-card-identity">
                                    <span className="qt-symbol">{group.underlying_symbol}</span>
                                    <span className="qt-action-badge">{label}</span>
                                    {group.total_contracts > 1 && (
                                        <span className="qt-contracts-badge">
                                            ×{group.total_contracts}
                                        </span>
                                    )}
                                </div>
                                <motion.button
                                    className="qt-cancel-btn"
                                    onClick={() => handleCancelGroup(group)}
                                    disabled={isCancelling || queueState.status === "submitting"}
                                    whileHover={{ scale: isCancelling || queueState.status === "submitting" ? 1 : 1.03 }}
                                    whileTap={{ scale: isCancelling || queueState.status === "submitting" ? 1 : 0.96 }}
                                    transition={{ duration: 0.12, ease: "easeOut" }}
                                >
                                    {isCancelling ? "…" : queueState.status === "submitting" ? "Submitting…" : "Cancel"}
                                </motion.button>
                            </div>

                            {/* Contract details */}
                            {contractLine && (
                                <div className="qt-contract-line">{contractLine}</div>
                            )}

                            {/* Aggregated financials */}
                            <div className="qt-financials">
                                <div className="qt-financial-item">
                                    <span className="qt-financial-label">Total premium</span>
                                    <span className="qt-financial-value qt-financial-green">
                                        +${fmtCurrency(group.total_premium)}
                                    </span>
                                </div>
                                {group.capital_required > 0 && (
                                    <div className="qt-financial-item">
                                        <span className="qt-financial-label">Capital required</span>
                                        <span className="qt-financial-value">
                                            ${fmtCurrency(group.capital_required)}
                                        </span>
                                    </div>
                                )}
                                {group.total_contracts > 1 && (
                                    <div className="qt-financial-item">
                                        <span className="qt-financial-label">Contracts</span>
                                        <span className="qt-financial-value">
                                            {group.total_contracts}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Reason */}
                            {reasonText && (
                                <div className="qt-reason">{reasonText}</div>
                            )}

                            {/* Strategy / mode footer */}
                            {(strategyLabel || modeLabel) && (
                                <div className="qt-card-footer">
                                    {strategyLabel && (
                                        <span className="qt-strategy-label">{strategyLabel}</span>
                                    )}
                                    {modeLabel && (
                                        <span className={`qt-mode-badge qt-mode-badge--${modeLabel}`}>
                                            {modeLabel === "auto" ? "Auto" : "Manual"}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Status footer */}
                            <div className="qt-status-row">
                                <div className="qt-status">
                                    <span className={`qt-status-dot qt-status-dot--${queueState.status}`} />
                                    {queueState.note}
                                </div>
                                {group.created_at && (
                                    <span className="qt-queued-at">
                                        {queueState.label} {fmtTime(group.created_at)}
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>

        </section>
    );
};

export default QueuedTrades;
